import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Helmet from '../lib/Helmet';
import { addToCart, clearCart, readCart } from '../lib/cart';
import { saveCustomer, savePincode } from '../lib/customer';
import { BUSINESS } from '../data/pricing';

// Route: /resume?r=<ref>&o=<offer>&s=<signature>
//
// The "Complete my order" button in the reminder and offer emails lands here.
// It rebuilds the cart from the saved order, on whatever device the email is
// opened, fills in the delivery details already typed, keeps the offer if
// there is one, and goes straight to checkout.

const OFFER_KEY = 'cc_offer';

export default function Resume() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [problem, setProblem] = useState('');

  useEffect(() => {
    const qs = new URLSearchParams({ r: params.get('r') || '', o: params.get('o') || '', s: params.get('s') || '' });
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/resume?${qs}`);
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data.ok) { setProblem(data.error || 'We could not open your order.'); return; }

        clearCart();
        data.lines.forEach((l) => addToCart(l.modelId, l.kit, l.qty));
        if (!readCart().length) {
          setProblem('The AC in your order has just sold out. Our team can suggest the closest alternative.');
          return;
        }

        const c = data.customer || {};
        if (c.mobile) {
          saveCustomer({
            mobile: c.mobile, fullName: c.fullName, email: c.email, gstin: '', company: '',
            address: { pincode: c.pincode, house: c.house, floor: '', lift: '', area: c.area, landmark: c.landmark, label: 'Home' },
          });
        }
        if (c.pincode) savePincode(c.pincode);

        try {
          if (data.offer) localStorage.setItem(OFFER_KEY, JSON.stringify(data.offer));
        } catch { /* storage off: checkout still works, without the offer shown */ }

        navigate('/checkout', { replace: true, state: { offerProblem: data.offerProblem || '' } });
      } catch {
        if (!cancelled) setProblem('We could not open your order. Check your connection and try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [params, navigate]);

  return (
    <main className="mx-auto max-w-xl px-4 pb-16 pt-28 text-center">
      <Helmet>
        <title>Your order | Citi Cool</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      {!problem ? (
        <p className="text-base font-semibold text-gray-700">Opening your order…</p>
      ) : (
        <>
          <h1 className="text-xl font-bold text-gray-900">{problem}</h1>
          <p className="mt-2 text-sm text-gray-600">Call our sales team on {BUSINESS.phone} and we&apos;ll sort it out.</p>
          <Link to="/daikin-ac-price-list-delhi"
            className="mt-5 inline-block rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700">
            Browse Daikin ACs
          </Link>
        </>
      )}
    </main>
  );
}
